import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { StorageUtil } from '../utils/storage.util';

export interface CapabilityDTO {
  id: number;
  code: string;
  category: string;
  description: string;
}

export interface UserAccessDTO {
  userId: number;
  marinaId: number;
  profile: 'ADMIN' | 'VIEWER' | 'CUSTOM';
  capabilityCodes: string[];
  /**
   * EAS-327 — true = permissão própria gravada; false = valor derivado do perfil. Só vem quando o
   * backend está com o EAS-327 ligado: sem ele o campo não existe na resposta.
   */
  explicit?: boolean;
}

/** EAS-327 — capacidades configuradas num perfil (tela de cadastro do perfil). */
export interface RoleAccessDTO {
  roleId: number;
  systemDefault: boolean;
  capabilityCodes: string[];
}

/**
 * EAS-327 — o backend respondeu com o modelo novo? O sinal é o campo `explicit` no acesso do
 * usuário: ele só existe com o EAS-327 ligado no backend. Sem ele, `DELETE .../user/{u}/marina/{m}`
 * e `.../role/{id}/capabilities` nem existem (404), então a tela fica como hoje.
 */
export function hasRoleCapabilities(access: UserAccessDTO): boolean {
  return !!access && typeof access.explicit === 'boolean';
}

/**
 * EAS-198 — controle de acesso por capacidade (api-security).
 * EAS-327 — capacidade por perfil + somente visualização por usuário.
 */
@Injectable({ providedIn: 'root' })
export class AccessControlService {

  private readonly base = environment.apiHost + '/api-security/secure/access';

  /** Resposta do teste do EAS-327, uma por marina (o backend não muda durante a sessão). */
  private roleCapabilitiesProbe: { marinaId: number; enabled: Observable<boolean> } = null;

  constructor(private http: HttpClient) {}

  getCatalog(): Observable<CapabilityDTO[]> {
    return this.http.get<CapabilityDTO[]>(`${this.base}/catalog`);
  }

  getUserAccess(userId: number, marinaId: number): Observable<UserAccessDTO> {
    return this.http.get<UserAccessDTO>(`${this.base}/user/${userId}/marina/${marinaId}`);
  }

  setUserAccess(userId: number, marinaId: number, body: { profile: string; capabilityCodes: string[] }): Observable<UserAccessDTO> {
    return this.http.put<UserAccessDTO>(`${this.base}/user/${userId}/marina/${marinaId}`, body);
  }

  /**
   * EAS-327 — o backend já está com "somente visualização" e capacidades por perfil? Lê o acesso do
   * próprio usuário logado (rota que já existe em produção) e olha o campo `explicit`. Qualquer
   * erro conta como "não": a tela continua com o cadeado e sem as opções novas.
   */
  roleCapabilitiesEnabled(): Observable<boolean> {
    const marinaId = StorageUtil.getMarinaId();
    if (!this.roleCapabilitiesProbe || this.roleCapabilitiesProbe.marinaId !== marinaId) {
      this.roleCapabilitiesProbe = { marinaId, enabled: this.probeRoleCapabilities(marinaId) };
    }
    return this.roleCapabilitiesProbe.enabled;
  }

  private probeRoleCapabilities(marinaId: number): Observable<boolean> {
    let userId: number;
    try {
      userId = StorageUtil.getUserId();
    } catch {
      userId = null;
    }
    if (!userId || !marinaId) {
      return of(false);
    }
    return this.getUserAccess(userId, marinaId).pipe(
      map((access) => hasRoleCapabilities(access)),
      catchError(() => of(false)),
      shareReplay(1)
    );
  }

  /**
   * EAS-327 — Liga o "somente visualização": grava acesso explícito VIEWER, que tem prioridade
   * sobre o recorte do perfil.
   */
  setViewOnly(userId: number, marinaId: number): Observable<UserAccessDTO> {
    return this.setUserAccess(userId, marinaId, { profile: 'VIEWER', capabilityCodes: [] });
  }

  /**
   * EAS-327 — Desliga o "somente visualização": remove o acesso explícito e devolve o usuário ao
   * recorte do perfil atribuído.
   */
  clearUserAccess(userId: number, marinaId: number): Observable<UserAccessDTO> {
    return this.http.delete<UserAccessDTO>(`${this.base}/user/${userId}/marina/${marinaId}`);
  }

  getRoleAccess(roleId: number): Observable<RoleAccessDTO> {
    return this.http.get<RoleAccessDTO>(`${this.base}/role/${roleId}/capabilities`);
  }

  setRoleAccess(roleId: number, capabilityCodes: string[]): Observable<RoleAccessDTO> {
    return this.http.put<RoleAccessDTO>(`${this.base}/role/${roleId}/capabilities`, { capabilityCodes });
  }
}
